import { Component, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ButtonComponent } from '@shared/components/button';
import { FormFieldComponent } from '@shared/components/form-field';
import { InputComponent } from '@shared/components/input';
import { ModalFooterDirective, ModalShellComponent } from '@shared/components/modal';
import { LONGUEURS_MAXIMALES, NatureServiceConnect } from '../../liste-clients.interface';

export interface DialogPartenaireConnectData {
  // Type Tick&Live Connect (famille d'étiquettes) auquel le partenaire appartient
  type: string;
  // Partenaire (PartnerConfigurations, défaut), application cliente (ClientConfigurations) ou front
  // web (FrontConfigurations) : mêmes champs, seuls les libellés changent
  nature?: NatureServiceConnect;
  partnerName?: string;
  clientId?: string;
}

export interface DialogPartenaireConnectOutput {
  partnerName: string;
  clientId?: string;
}

const LIBELLES: Record<NatureServiceConnect, { nom: string; creation: string; modification: string }> = {
  partenaire: { nom: 'Libellé du partenaire', creation: 'Nouveau partenaire', modification: 'Modifier le partenaire' },
  application: {
    nom: "Libellé de l'application",
    creation: 'Nouvelle application',
    modification: "Modifier l'application",
  },
  front: { nom: 'Libellé du front', creation: 'Nouveau front', modification: 'Modifier le front' },
};

// Dialog "dumb" : informations du partenaire (création quand data est vide, modification sinon)
@Component({
  selector: 'app-dialog-partenaire-connect',
  imports: [
    ModalShellComponent,
    ModalFooterDirective,
    ButtonComponent,
    FormFieldComponent,
    InputComponent,
    ReactiveFormsModule,
  ],
  templateUrl: './dialog-partenaire-connect.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './dialog-partenaire-connect.component.scss',
})
export class DialogPartenaireConnectComponent {
  // Bornes de saisie partagées avec le backend : une valeur trop longue serait refusée,
  // et surtout la modification ne pourrait pas être enregistrée dans le journal d'audit
  protected readonly longueurs = LONGUEURS_MAXIMALES;

  readonly dialogRef = inject<DialogRef<DialogPartenaireConnectOutput, DialogPartenaireConnectComponent>>(DialogRef);
  readonly data = inject<DialogPartenaireConnectData>(DIALOG_DATA);

  partenaireForm = new FormGroup({
    partnerName: new FormControl<string>(this.data.partnerName ?? '', { nonNullable: true }),
    clientId: new FormControl<string>(this.data.clientId ?? '', { nonNullable: true }),
  });

  estCreation = !this.data.partnerName;

  private libelles = LIBELLES[this.data.nature ?? 'partenaire'];

  libelleNom = this.libelles.nom;

  titre = (this.estCreation ? this.libelles.creation : this.libelles.modification) + ' — ' + this.data.type;

  annuler(): void {
    this.dialogRef.close();
  }

  private readonly nomSaisi = toSignal(this.partenaireForm.controls.partnerName.valueChanges, {
    initialValue: this.partenaireForm.controls.partnerName.value,
  });

  readonly peutValider = computed(() => !!this.nomSaisi().trim());

  valider() {
    if (!this.peutValider()) {
      return;
    }

    this.dialogRef.close({
      partnerName: this.partenaireForm.controls.partnerName.value.trim(),
      clientId: this.partenaireForm.controls.clientId.value || undefined,
    });
  }
}
